import { currencies } from 'constants/currenciesList';
import { LOCATION_USER_ROLES } from 'constants/locationUserRoles';
import i18n from 'i18n';
import * as Yup from 'yup';

const hasValue = (value) => !!value?.trim();

const isValidHttpUrl = (value) => {
  if (!hasValue(value)) return true;

  try {
    const url = new URL(value.trim());
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
};

const additionalLinkSchema = Yup.object({
  name: Yup.string(),
  url: Yup.string()
    .max(500, i18n.t('validation:atMostChars', { number: 500 }))
    .test('valid-http-url', i18n.t('validation:invalidUrl'), isValidHttpUrl),
}).test('complete-additional-link', i18n.t('validation:required'), function validateLink(link) {
  const hasName = hasValue(link?.name);
  const hasUrl = hasValue(link?.url);

  if (hasName === hasUrl) return true;

  return this.createError({
    path: `${this.path}.${hasName ? 'url' : 'name'}`,
    message: i18n.t('validation:required'),
  });
});

export default {
  event: Yup.object({
    name: Yup.string()
      .min(3, i18n.t('validation:atLeastChars', { number: 3 }))
      .required(i18n.t('validation:required')),
    date: Yup.string().required(i18n.t('validation:required')),
    share_link_on_retailer_events_tab: Yup.boolean(),
    require_auth_for_comments: Yup.boolean(),
    require_auth_to_join: Yup.boolean(),
  }),
  size: Yup.object({
    name: Yup.string()
      .min(1, i18n.t('validation:atLeastChars', { number: 1 }))
      .required(i18n.t('validation:required')),
  }),
  region: Yup.object({
    name: Yup.string()
      .min(3, i18n.t('validation:atLeastChars', { number: 3 }))
      .required(i18n.t('validation:required')),
    subdomain: Yup.string()
      .matches(/^[a-zA-Z0-9]+$/, {
        message: i18n.t('validation:lettersAndNumbersOnly'),
        excludeEmptyString: true,
      })
      .required(i18n.t('validation:required')),
    base_currency: Yup.mixed()
      .oneOf(
        currencies.map(({ code }) => code),
        i18n.t('validation:currencyCodeNotValid'),
      )
      .required(i18n.t('validation:required')),
  }),
  regionUpdate: Yup.object({
    name: Yup.string()
      .min(3, i18n.t('validation:atLeastChars', { number: 3 }))
      .required(i18n.t('validation:required')),
    subdomain: Yup.string()
      .matches(/^[a-zA-Z0-9]+$/, {
        message: i18n.t('validation:lettersOnly'),
        excludeEmptyString: true,
      })
      .required(i18n.t('validation:required')),
  }),
  parent_product: Yup.object({
    name: Yup.string().required(i18n.t('validation:required')),
    description: Yup.string().required(i18n.t('validation:required')),
    details: Yup.string().required(i18n.t('validation:required')),
    category_uid: Yup.string().required(i18n.t('validation:required')),
  }),
  child_product: Yup.object({
    product_code: Yup.string().required(i18n.t('validation:required')),
    description: Yup.string()
      .max(5000, i18n.t('validation:atMostChars', { number: 5000 }))
      .required(i18n.t('validation:required')),
    thumbnail: Yup.array()
      .min(1, i18n.t('validation:minElements', { number: 1 }))
      .required(i18n.t('validation:required')),
    parent_product_uid: Yup.string().required(i18n.t('validation:required')),
    color_uid: Yup.string().nullable(),
    size_uid: Yup.string().nullable(),
    inventory_uid: Yup.string().required(i18n.t('validation:required')),
  }).test('color-or-size-required', '', function colorOrSizeRequired(value) {
    const { color_uid, size_uid } = value;
    const message = i18n.t('validation:requiredEitherColorOrSize');

    if (!color_uid && !size_uid) {
      return (
        this.createError({
          path: 'color_uid',
          message,
        }) &&
        this.createError({
          path: 'size_uid',
          message,
        })
      );
    }

    return true;
  }),
  color: Yup.object({
    name: Yup.string()
      .min(3, i18n.t('validation:atLeastChars', { number: 3 }))
      .required(i18n.t('validation:required')),
    hex_value: Yup.string()
      .min(3, i18n.t('validation:atLeastChars', { number: 3 }))
      .required(i18n.t('validation:required')),
  }),
  category: Yup.object({
    name: Yup.string()
      .min(3, i18n.t('validation:atLeastChars', { number: 3 }))
      .required(i18n.t('validation:required')),
  }),
  inventory: Yup.object({
    name: Yup.string()
      .min(3, i18n.t('validation:atLeastChars', { number: 3 }))
      .required(i18n.t('validation:required')),
  }),
  location_create: Yup.object({
    name: Yup.string()
      // TODO: update to match backend validation
      .max(100, i18n.t('validation:atMostChars', { number: 100 }))
      .required(i18n.t('validation:required')),
    description: Yup.string()
      // TODO: update to match backend validation
      .max(250, i18n.t('validation:atMostChars', { number: 250 })),
    regionUid: Yup.string().required(i18n.t('validation:required')),
    vanityUrl: Yup.string()
      .min(6, i18n.t('validation:atLeastChars', { number: 6 }))
      .matches(/^[a-zA-Z0-9-_]+$/, i18n.t('validation:lettersNumbersHyphensAndUnderscoresOnly'))
      .required(i18n.t('validation:required')),
    inviteOwnerEmail: Yup.string().email(i18n.t('validation:invalidEmail')),
  }),
  location_group_create: Yup.object({
    name: Yup.string()
      .max(255, i18n.t('validation:atMostChars', { number: 255 }))
      .required(i18n.t('validation:required')),
    vanityUrl: Yup.string()
      .min(3, i18n.t('validation:atLeastChars', { number: 3 }))
      .matches(/^[a-zA-Z0-9-_]+$/, i18n.t('validation:lettersNumbersHyphensAndUnderscoresOnly'))
      .required(i18n.t('validation:required')),
    logo: Yup.mixed().nullable(),
    coverImage: Yup.mixed().nullable(),
    locations: Yup.array(),
  }),
  location_user: Yup.object().shape({
    uid: Yup.string().required(i18n.t('validation:required')),
    role: Yup.string()
      .oneOf(Object.values(LOCATION_USER_ROLES), i18n.t('validation:required'))
      .required(i18n.t('validation:required')),
  }),
  location_info: Yup.object({
    name: Yup.string()
      // TODO: update to match backend validation
      .max(100, i18n.t('validation:atMostChars', { number: 100 }))
      .required(i18n.t('validation:required')),
    description: Yup.string()
      // TODO: update to match backend validation
      .max(250, i18n.t('validation:atMostChars', { number: 250 })),
    vanityUrl: Yup.string()
      .min(6, i18n.t('validation:atLeastChars', { number: 6 }))
      .matches(/^[a-zA-Z0-9-_]+$/, i18n.t('validation:lettersNumbersHyphensAndUnderscoresOnly'))
      .required(i18n.t('validation:required')),
  }),
  location_address: Yup.object({
    street: Yup.string().required(i18n.t('validation:required')),
    apartment: Yup.string().nullable(),
    city: Yup.string().required(i18n.t('validation:required')),
    zip: Yup.string().required(i18n.t('validation:required')),
    state: Yup.string().nullable(),
    country: Yup.string().required(i18n.t('validation:required')),
  }),
  location_socials: Yup.object({
    facebook: Yup.string(),
    instagram: Yup.string(),
    tiktok: Yup.string(),
    x: Yup.string(),
    pinterest: Yup.string(),
  }),
  location_additional_links: Yup.object({
    additional_links: Yup.array().of(additionalLinkSchema),
  }),
  location_images: Yup.object({
    logoId: Yup.array()
      .min(1, i18n.t('validation:minElements', { number: 1 }))
      .required(i18n.t('validation:required')),
    coverImageId: Yup.array()
      .min(1, i18n.t('validation:minElements', { number: 1 }))
      .required(i18n.t('validation:required')),
  }),
  checkout: Yup.object().shape({
    destinationUrl: Yup.string()
      .max(500, i18n.t('validation:atMostChars', { number: 500 }))
      .url(i18n.t('validation:invalidUrl'))
      .required(i18n.t('validation:required')),
    locationParameterName: Yup.string()
      .max(100, i18n.t('validation:atMostChars', { number: 100 }))
      .matches(/^[a-zA-Z0-9-_]+$/, i18n.t('validation:lettersNumbersHyphensAndUnderscoresOnly')),
    publisherParameterName: Yup.string()
      .max(100, i18n.t('validation:atMostChars', { number: 100 }))
      .matches(/^[a-zA-Z0-9-_]+$/, i18n.t('validation:lettersNumbersHyphensAndUnderscoresOnly')),
    brandTrackingParameterName: Yup.string()
      .max(100, i18n.t('validation:atMostChars', { number: 100 }))
      .matches(/^[a-zA-Z0-9-_]+$/, i18n.t('validation:lettersNumbersHyphensAndUnderscoresOnly')),
    redirectUrlParameterName: Yup.string().when('redirectUrl', {
      is: (val) => !!val && val.trim() !== '',
      then: (schema) =>
        schema
          .max(100, i18n.t('validation:atMostChars', { number: 100 }))
          .matches(/^[a-zA-Z0-9-_]+$/, i18n.t('validation:lettersNumbersHyphensAndUnderscoresOnly'))
          .required(i18n.t('validation:required')),
    }),
    redirectUrl: Yup.string()
      .max(500, i18n.t('validation:atMostChars', { number: 500 }))
      .url(i18n.t('validation:invalidUrl')),
    customParameters: Yup.array().of(
      Yup.object({
        key: Yup.string().required(i18n.t('validation:required')),
        value: Yup.string().required(i18n.t('validation:required')),
      }),
    ),
    productParameters: Yup.array()
      .of(
        Yup.object({
          productField: Yup.string().required(i18n.t('validation:required')),
          parameterName: Yup.string().required(i18n.t('validation:required')),
        }),
      )
      .test(
        'required-product-keys',
        i18n.t('validation:missingRequiredProductKeys'),
        (productParameters) => {
          const productKeys = productParameters?.map((p) => p.productField) || [];
          const requiredProductKeys = ['product_code'];
          return requiredProductKeys.every((requiredKey) => productKeys.includes(requiredKey));
        },
      ),
    productEncoding: Yup.string()
      .oneOf(['indexed', 'comma', 'json'], i18n.t('validation:invalidProductEncoding'))
      .required(i18n.t('validation:required')),
    quantityParameterName: Yup.string()
      .max(100, i18n.t('validation:atMostChars', { number: 100 }))
      .matches(/^[a-zA-Z0-9-_]+$/, i18n.t('validation:lettersNumbersHyphensAndUnderscoresOnly'))
      .required(i18n.t('validation:required')),
  }),
};
